import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaVideo, FaStop, FaRedo, FaUpload, FaCheckCircle,
  FaTimesCircle, FaCamera, FaMicrophone, FaFileVideo, FaTimes
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * VideoRecorder
 * Props:
 *   video   – { _id, title }
 *   lesson  – { _id, title }
 *   onClose – called when modal should close
 *   onSuccess – called after successful submission
 */
const VideoRecorder = ({ video, lesson, onClose, onSuccess }) => {
  const [step, setStep] = useState('permission'); // permission | record | preview | upload | done | error
  const [permissionError, setPermissionError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [useFileUpload, setUseFileUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [elapsedSecs, setElapsedSecs] = useState(0);

  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const liveVideoRef = useRef(null);
  const timerRef = useRef(null);
  const token = localStorage.getItem('token');

  // Request camera + mic
  const requestPermission = useCallback(async () => {
    setPermissionError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setStep('record');
    } catch (err) {
      setPermissionError(
        err.name === 'NotAllowedError'
          ? 'Camera/microphone permission denied. Please allow access in your browser and try again.'
          : `Could not access camera/mic: ${err.message}`
      );
    }
  }, []);

  // Attach stream to live preview element
  useEffect(() => {
    if (step === 'record' && liveVideoRef.current && streamRef.current) {
      liveVideoRef.current.srcObject = streamRef.current;
    }
  }, [step]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      stopStream();
      clearInterval(timerRef.current);
    };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = () => {
    chunksRef.current = [];
    const options = { mimeType: 'video/webm;codecs=vp8,opus' };
    let mr;
    try {
      mr = new MediaRecorder(streamRef.current, options);
    } catch {
      mr = new MediaRecorder(streamRef.current);
    }
    mediaRecorderRef.current = mr;
    mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setRecordedUrl(url);
      stopStream();
      setStep('preview');
    };
    mr.start(250);
    setRecording(true);
    setElapsedSecs(0);
    timerRef.current = setInterval(() => setElapsedSecs(s => s + 1), 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    mediaRecorderRef.current?.stop();
  };

  const reRecord = async () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setUploadError(null);
    // Re-acquire stream
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setStep('record');
    } catch (err) {
      setPermissionError(`Could not re-access camera: ${err.message}`);
      setStep('permission');
    }
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowed.includes(file.type)) { alert('Please select a valid video file (MP4, MOV, AVI, WebM).'); return; }
    setSelectedFile(file);
  };

  const submitVideo = async (blobOrFile, filename = 'practice.webm') => {
    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    setStep('upload');

    const formData = new FormData();
    formData.append('video', blobOrFile, filename);
    formData.append('lessonId', lesson._id);
    formData.append('videoId', video._id);

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', e => {
      if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 201) {
        setStep('done');
        if (onSuccess) onSuccess();
      } else {
        try {
          const d = JSON.parse(xhr.responseText);
          setUploadError(d.message || 'Upload failed');
        } catch { setUploadError('Upload failed'); }
        setStep('preview');
      }
    };
    xhr.onerror = () => { setUploading(false); setUploadError('Network error. Please try again.'); setStep('preview'); };
    xhr.open('POST', `${API_BASE}/submissions/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  };

  const handleSubmitRecorded = () => submitVideo(recordedBlob, `practice_${Date.now()}.webm`);
  const handleSubmitFile = () => { if (selectedFile) submitVideo(selectedFile, selectedFile.name); };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-900">
          <div>
            <p className="text-white font-bold text-sm">{lesson?.title}</p>
            <p className="text-gray-400 text-xs mt-0.5">Submit practice for: <span className="text-orange-400">{video?.title}</span></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition text-xl">
            <FaTimes />
          </button>
        </div>

        <div className="p-6">

          {/* STEP: Permission */}
          {step === 'permission' && (
            <div className="text-center py-4">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center">
                  <FaCamera className="text-3xl text-orange-500" />
                </div>
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                  <FaMicrophone className="text-3xl text-blue-500" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Allow Camera & Microphone</h3>
              <p className="text-gray-500 text-sm mb-6">
                To record your practice video, we need access to your camera and microphone. Click the button below and allow access when prompted.
              </p>
              {permissionError && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                  <FaTimesCircle className="flex-shrink-0" /> {permissionError}
                </div>
              )}
              <button
                onClick={requestPermission}
                className="w-full py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition flex items-center justify-center gap-2"
              >
                <FaCamera /> Allow Camera & Mic Access
              </button>
              <button
                onClick={() => setUseFileUpload(true)}
                className="mt-3 w-full py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition flex items-center justify-center gap-2"
              >
                <FaFileVideo /> Upload a video file instead
              </button>

              {/* File upload fallback */}
              {useFileUpload && (
                <div className="mt-4 text-left">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select video file</label>
                  <input type="file" accept="video/*" onChange={handleFileChange} className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" />
                  {selectedFile && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</p>
                      </div>
                      <button onClick={handleSubmitFile} className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition">
                        Submit
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP: Record */}
          {step === 'record' && (
            <div>
              <div className="relative bg-black rounded-xl overflow-hidden mb-4" style={{ aspectRatio: '16/9' }}>
                <video ref={liveVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                {recording && (
                  <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    REC {formatTime(elapsedSecs)}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                {!recording ? (
                  <button
                    onClick={startRecording}
                    className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition flex items-center justify-center gap-2"
                  >
                    <FaVideo /> Start Recording
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="flex-1 py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-900 transition flex items-center justify-center gap-2"
                  >
                    <FaStop /> Stop Recording
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 text-center mt-3">
                Record yourself playing — when done, click Stop to preview before submitting.
              </p>
            </div>
          )}

          {/* STEP: Preview */}
          {step === 'preview' && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Preview Your Recording</h3>
              {uploadError && (
                <div className="mb-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                  <FaTimesCircle /> {uploadError}
                </div>
              )}
              <div className="bg-black rounded-xl overflow-hidden mb-4" style={{ aspectRatio: '16/9' }}>
                <video src={recordedUrl} controls className="w-full h-full object-contain" />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={reRecord}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition flex items-center justify-center gap-2"
                >
                  <FaRedo /> Re-record
                </button>
                <button
                  onClick={handleSubmitRecorded}
                  className="flex-1 py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition flex items-center justify-center gap-2"
                >
                  <FaUpload /> Submit to Teacher
                </button>
              </div>
            </div>
          )}

          {/* STEP: Uploading */}
          {step === 'upload' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="font-semibold text-gray-800 mb-4">Uploading your practice video...</p>
              <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                <div
                  className="bg-orange-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-orange-600 font-medium">{uploadProgress}%</p>
              <p className="text-xs text-gray-400 mt-2">Please keep this window open while uploading.</p>
            </div>
          )}

          {/* STEP: Done */}
          {step === 'done' && (
            <div className="text-center py-8">
              <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Submitted Successfully!</h3>
              <p className="text-gray-500 text-sm mb-6">
                Your practice video has been sent to your teacher for review and grading.
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition"
              >
                Done
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default VideoRecorder;
